import { ref } from 'vue'
import type { DeckPlayerCardChangeAnimation } from '../../types'
import type { ShowingMedia } from './media'
import { EMPTY_REGISTERS } from './registers'

/** Reactive state of one flowchart session (cursor, pools, registers, pauses). */
export function createFlowSessionState() {
  return {
    cursorNodeId: ref<string | null>(null),
    activeGroupId: ref<string | null>(null),
    activePhaseId: ref<string | null>(null),
    /** groupId → card ids not yet pulled. */
    pools: ref<Record<string, string[]>>({}),
    currentCardId: ref<string | null>(null),
    showBackFirst: ref(false),
    doneCardIds: ref<Set<string>>(new Set()),
    complete: ref(false),
    registers: ref(EMPTY_REGISTERS()),
    /** Per-card checkbox values tracked by the flow (conditions, phase saves). */
    cardCheckboxValues: ref<Map<string, boolean[]>>(new Map()),
    /** set-value overrides of the progress bar; null = automatic count. */
    progressTotal: ref<number | null>(null),
    progressCurrent: ref<number | null>(null),
    /** Resumes the flow in manual/paused mode or after a debug pause. */
    manualContinuation: ref<(() => void) | null>(null),
    needsUserInteraction: ref(false),
    /** First load outside the editor waits for a Start gesture (unlocks audio autoplay). */
    awaitingStart: ref(false),
    /** Incremented by every start; timers of an older epoch are ignored. */
    epoch: ref(0),
    /** True during the synchronous pass of a start: phase nodes don't pause there. */
    isInitializing: ref(false),
    phaseInterlude: ref(false),
    phaseResume: ref<(() => void) | null>(null),
    showingMedia: ref<ShowingMedia | null>(null),
    showMediaResume: ref<(() => void) | null>(null),
    cardTransition: ref<DeckPlayerCardChangeAnimation>('none'),
    /** An "Ask: known?" node waiting for the learner's ✓ / ✗ answer. */
    awaitingAnswer: ref<{ nodeId: string; cardId: string } | null>(null),
    /** Answers of this run: cardId → known. A card answered twice keeps the last answer. */
    cardAnswers: ref<Map<string, boolean>>(new Map()),
    /** Signals raised by the flow for the host (e.g. 'offer-practice:practice'). */
    flowSignals: ref<string[]>([]),
    /** Macro calls: each frame remembers the calling macro-call node. */
    callStack: ref<Array<{ callNodeId: string; entryPort: string }>>([]),
  }
}

export type FlowSessionState = ReturnType<typeof createFlowSessionState>

/** Clears the session when the player switches to another deck. */
export function resetForDeckChange(state: FlowSessionState) {
  state.cursorNodeId.value = null
  state.activeGroupId.value = null
  state.activePhaseId.value = null
  state.pools.value = {}
  state.currentCardId.value = null
  state.showBackFirst.value = false
  state.doneCardIds.value = new Set()
  state.complete.value = false
  state.registers.value = EMPTY_REGISTERS()
  state.cardCheckboxValues.value = new Map()
  state.progressTotal.value = null
  state.progressCurrent.value = null
  state.phaseInterlude.value = false
  state.phaseResume.value = null
  state.cardTransition.value = 'none'
  state.callStack.value = []
  state.awaitingAnswer.value = null
  state.cardAnswers.value = new Map()
  state.flowSignals.value = []
}

/** Clears everything a (re)start rebuilds, and cancels pending timers via the epoch. */
export function resetForStart(state: FlowSessionState) {
  state.epoch.value++
  state.cursorNodeId.value = null
  state.activeGroupId.value = null
  state.activePhaseId.value = null
  state.currentCardId.value = null
  state.showBackFirst.value = false
  state.doneCardIds.value = new Set()
  state.complete.value = false
  state.registers.value = EMPTY_REGISTERS()
  state.progressTotal.value = null
  state.progressCurrent.value = null
  state.manualContinuation.value = null
  state.awaitingAnswer.value = null
  state.cardAnswers.value = new Map()
  state.flowSignals.value = []
}
